import _ from 'lodash';
import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Collapse,
    Divider
} from '@mui/material';
import {
    Gavel as GavelIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Add as AddIcon,
    ChevronRight as ChevronRightIcon,
    AccountBalance as AccountBalanceIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import SourceButton from '../PhoneTab/SourceButton.js';
import { colors, typography } from '../../styles/globals.js';
import { RemoveLitigationRecordButton } from '../EditMode/CompanyDetailEditControls';

export const toTitleCase = (str) => {
    if (!str) return str;
    return str.replace(/\b\w+/g, word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    );
};

const STATE_NAMES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
    HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
    MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
    MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
    SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
    VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    DC: 'District of Columbia', PR: 'Puerto Rico', VI: 'Virgin Islands', GU: 'Guam',
    AS: 'American Samoa', MP: 'Northern Mariana Islands',
};

const getStateName = (state) => {
    if (!state) return null;
    const upper = state.toUpperCase().trim();
    return STATE_NAMES[upper] || toTitleCase(state);
};

// Bullet item component matching risk signals style
export const BulletItem = ({ label, text, isHighlighted = false }) => {
    if (!text && !label) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.25, pl: 1 }}>
            <Box sx={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: isHighlighted ? '#DC2626' : colors.primary,
                mt: '7px',
                flexShrink: 0
            }} />
            <Typography sx={{
                fontSize: '0.875rem',
                color: isHighlighted ? '#DC2626' : '#374151',
                fontWeight: isHighlighted ? 600 : 400,
                letterSpacing: '0.25px',
                lineHeight: 1.5,
                wordBreak: 'break-word'
            }}>
                {label && <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit', color: 'inherit' }}>{label}:</Typography>}{label ? ` ${text}` : text}
            </Typography>
        </Box>
    );
};

// Section label within expanded details
export const DetailSectionLabel = ({ label }) => (
    <Typography sx={{
        color: '#374151',
        fontSize: '0.875rem',
        fontWeight: 600,
        mt: 1,
        mb: 0.25,
        pl: 1
    }}>
        {label}
    </Typography>
);

// Generate a case name from parties list in "Plaintiff Vs Defendant, Et Al." format
const generateCaseNameFromParties = (partiesList) => {
    if (!partiesList || partiesList.length === 0) return null;
    const plaintiffs = partiesList.filter(p => (p.role || '').toUpperCase().includes('PLAINTIFF'));
    const defendants = partiesList.filter(p => {
        const r = (p.role || '').toUpperCase();
        return r.includes('DEFEND');
    });
    if (plaintiffs.length === 0 && defendants.length === 0) return null;
    const plaintiffStr = plaintiffs.length > 0 ? plaintiffs[0].name : null;
    const defendantStr = defendants.length > 0 ? defendants[0].name : null;
    const otherCount = partiesList.length - (plaintiffStr ? 1 : 0) - (defendantStr ? 1 : 0);
    const etAl = otherCount > 0 ? ', Et Al.' : '';
    if (plaintiffStr && defendantStr) return `${plaintiffStr} Vs ${defendantStr}${etAl}`;
    if (plaintiffStr) return `${plaintiffStr}${etAl}`;
    if (defendantStr) return `${defendantStr}${etAl}`;
    return null;
};

// Card for Civil Court Records with detailed information
const CivilCourtRecordCard = ({
    caseNumber,
    caseName,
    caseType,
    caseSubtype,
    originalCaseType,
    partyType,
    partyName,
    allParties,
    partiesList,
    causeOfAction,
    filingDate,
    jurisdiction,
    state,
    county,
    city,
    caseEvents = [],
    caseEventsDetailed = [],
    status,
    source = [],
    entityTypeDescription,
    entityAddresses = [],
    alsoShownIn = [],
    onBadgeClick,
    courtLevel,
    // Financial details
    suitAmount,
    awardAmount,
    // Judgment details
    judgmentDate,
    judgmentType,
    judgmentDisposition,
    disposition,
    dispositionDate,
    ruling
}) => {
    const isOpen = status === 'open';
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);

    // Format location string
    const formatLocation = () => {
        const parts = _.filter([county, city, state], Boolean);
        return parts.length > 0 ? parts.join(', ') : null;
    };

    const location = formatLocation();

    // Count detail items for the toggle indicator
    const countDetailItems = () => {
        let count = 0;
        if (originalCaseType) count++;
        if (jurisdiction) count++;
        if (location) count++;
        if (causeOfAction) count++;
        if (entityTypeDescription) count++;
        if (suitAmount) count++;
        if (awardAmount) count++;
        if (judgmentType || disposition || ruling) count++;
        if (partiesList?.length > 0) count += partiesList.length;
        if (entityAddresses?.length > 0) count += Math.min(entityAddresses.length, 2);
        const events = caseEventsDetailed?.length > 0 ? caseEventsDetailed : caseEvents;
        if (events?.length > 0) count += Math.min(events.length, 3);
        return count;
    };
    const detailItemCount = countDetailItems();

    // Build court label: "California Civil Case", "Federal Civil Case", or "Civil Case"
    const getCourtLabel = () => {
        if (courtLevel === 'federal') return 'Federal Civil Case';
        const j = (jurisdiction || '').toUpperCase();
        const federalKeywords = ['U.S.', 'UNITED STATES', 'FEDERAL', 'BANKRUPTCY', 'CIRCUIT COURT OF APPEALS', 'DISTRICT COURT'];
        if (federalKeywords.some(kw => j.includes(kw))) return 'Federal Civil Case';
        if (state) return `${getStateName(state)} Civil Case`;
        return 'Civil Case';
    };
    const courtLabel = getCourtLabel();

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#F8FAFC',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                width: 360,
                maxWidth: '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        backgroundColor: '#2563EB',
                        borderRadius: '6px',
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                    }}>
                        <GavelIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: '#1E293B',
                            fontSize: '1rem',
                            lineHeight: 1.3,
                            wordBreak: 'break-word'
                        }}>
                            {courtLabel}
                        </Typography>
                        {caseNumber && (
                            <Typography title={caseNumber} sx={{
                                color: '#64748B',
                                fontSize: '0.7rem',
                                fontFamily: 'monospace',
                                mt: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}>
                                Case #{caseNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {(filingDate || !isOpen) && (
                        <Chip
                            label={filingDate ? `Filed ${filingDate}` : 'Closed'}
                            size="small"
                            sx={{
                                backgroundColor: filingDate ? '#EFF6FF' : '#DCFCE7',
                                color: filingDate ? '#2563EB' : '#16A34A',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                height: '22px',
                            }}
                        />
                    )}
                    {detailItemCount > 0 && (
                        <Box
                            onClick={() => setDetailsExpanded(!detailsExpanded)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                '&:hover': { backgroundColor: '#EFF6FF' }
                            }}
                        >
                            <ChevronRightIcon sx={{
                                color: '#1E293B',
                                fontSize: 20,
                                transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Case Name */}
            {(caseName || generateCaseNameFromParties(partiesList)) && (
                <Typography sx={{
                    color: '#374151',
                    fontSize: '0.875rem',
                    fontWeight: 400,
                    lineHeight: 1.3
                }}>
                    {toTitleCase(caseName || generateCaseNameFromParties(partiesList))}
                </Typography>
            )}

            {/* Duplicate badge */}
            {alsoShownIn.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
                    {alsoShownIn.map((badge, i) => {
                        const label = typeof badge === 'string' ? badge : badge.label;
                        const targetName = typeof badge === 'string' ? null : badge.targetName;
                        return (
                            <Chip
                                key={i}
                                label={label}
                                size="small"
                                onClick={targetName && onBadgeClick ? (e) => { e.stopPropagation(); onBadgeClick(targetName); } : undefined}
                                sx={{
                                    backgroundColor: '#FEE2E2',
                                    color: '#DC2626',
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    height: '20px',
                                    cursor: targetName && onBadgeClick ? 'pointer' : 'default',
                                    '& .MuiChip-label': { px: 1 },
                                    '&:hover': targetName && onBadgeClick ? { backgroundColor: '#FECACA' } : {}
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Details Section */}
            <Collapse in={detailsExpanded}>
                <Divider sx={{ my: 1.5 }} />
                <Box>
                    {originalCaseType && <BulletItem label="Type" text={originalCaseType} />}
                    {jurisdiction && <BulletItem label="Court" text={jurisdiction} />}
                    {location && <BulletItem label="Location" text={location} />}
                    {causeOfAction && <BulletItem label="Cause" text={causeOfAction} />}
                    {entityTypeDescription && <BulletItem label="Entity" text={entityTypeDescription} />}
                    {suitAmount && <BulletItem label="Suit amount" text={`$${Number(suitAmount).toLocaleString()}`} />}
                    {awardAmount && <BulletItem label="Award amount" text={`$${Number(awardAmount).toLocaleString()}`} />}
                    {judgmentType && <BulletItem label="Judgment" text={judgmentType} />}
                    {judgmentDate && <BulletItem label="Judgment date" text={judgmentDate} />}
                    {judgmentDisposition && <BulletItem label="Disposition" text={judgmentDisposition} />}
                    {disposition && !judgmentDisposition && <BulletItem label="Disposition" text={disposition} />}
                    {dispositionDate && <BulletItem label="Disposition date" text={dispositionDate} />}
                    {ruling && <BulletItem label="Ruling" text={ruling} />}

                    {partiesList && partiesList.length > 0 && (() => {
                        const defendants = partiesList.filter(p => p.role?.toUpperCase().includes('DEFEND'));
                        const plaintiffs = partiesList.filter(p => p.role?.toUpperCase().includes('PLAINTIFF'));
                        const others = partiesList.filter(p => {
                            const r = (p.role || '').toUpperCase();
                            return !r.includes('DEFEND') && !r.includes('PLAINTIFF');
                        });
                        return (
                            <>
                                {defendants.length > 0 && (
                                    <>
                                        <DetailSectionLabel label="Defendants:" />
                                        {defendants.map((p, i) => (
                                            <BulletItem key={i} text={toTitleCase(p.name)} />
                                        ))}
                                    </>
                                )}
                                {plaintiffs.length > 0 && (
                                    <>
                                        <DetailSectionLabel label="Plaintiffs:" />
                                        {plaintiffs.map((p, i) => (
                                            <BulletItem key={i} text={toTitleCase(p.name)} />
                                        ))}
                                    </>
                                )}
                                {others.length > 0 && (
                                    <>
                                        <DetailSectionLabel label="Parties:" />
                                        {others.map((p, i) => (
                                            <BulletItem key={i} text={`${toTitleCase(p.name)}${p.role && p.role.toLowerCase() !== 'unknown' ? ` (${toTitleCase(p.role)})` : ''}`} />
                                        ))}
                                    </>
                                )}
                            </>
                        );
                    })()}
                    {entityAddresses && entityAddresses.length > 0 && (
                        <>
                            <DetailSectionLabel label="Addresses:" />
                            {entityAddresses.slice(0, 2).map((addr, idx) => (
                                <BulletItem key={idx} text={addr} />
                            ))}
                        </>
                    )}
                    {caseEventsDetailed && caseEventsDetailed.length > 0 ? (
                        <>
                            <DetailSectionLabel label="Recent Events:" />
                            {caseEventsDetailed.slice(0, 3).map((event, idx) => (
                                <BulletItem key={idx} text={`${event.type}${event.date ? ` (${new Date(event.date).toLocaleDateString()})` : ''}`} />
                            ))}
                        </>
                    ) : caseEvents && caseEvents.length > 0 && (
                        <>
                            <DetailSectionLabel label="Recent Events:" />
                            {caseEvents.slice(0, 3).map((event, idx) => (
                                <BulletItem key={idx} text={event} />
                            ))}
                        </>
                    )}
                </Box>
            </Collapse>

            {/* Source Button - always visible at bottom */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 'auto',
                pt: 1.5
            }}>
                <SourceButton sources={source} />
            </Box>
        </Paper>
    );
};

// Card for Bankruptcy Records
const BankruptcyRecordCard = ({
    caseNumber,
    chapter,
    originalChapter,
    filingDate,
    filingStatus,
    caseStatus,
    courtName,
    courtLocation,
    judgeName,
    filerType,
    debtors = [],
    trustees = [],
    statusHistory = [],
    meetingDate,
    meetingAddress,
    assetsForUnsecured,
    selfRepresented,
    status,
    source = [],
    alsoShownIn = [],
    onBadgeClick,
    partiesList = [],
}) => {
    const isOpen = status === 'open';
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);

    const chapterLabel = chapter ? `Chapter ${chapter} Bankruptcy` : 'Bankruptcy';

    // Deduplicate debtors by name (case-insensitive)
    const uniqueDebtors = debtors.filter((d, i, arr) =>
        arr.findIndex(x => x.name?.toLowerCase().trim() === d.name?.toLowerCase().trim()) === i
    );

    // Use first debtor's name as subtitle instead of filing status
    const subtitleName = uniqueDebtors.length > 0 ? toTitleCase(uniqueDebtors[0].name) : null;

    const countDetailItems = () => {
        let count = 0;
        if (courtName) count++;
        if (courtLocation) count++;
        if (judgeName) count++;
        if (filerType) count++;
        if (filingStatus || caseStatus) count++;
        if (uniqueDebtors.length > 0) count += uniqueDebtors.length;
        if (trustees.length > 0) count += trustees.length;
        if (statusHistory?.length > 0) count += Math.min(statusHistory.length, 3);
        if (meetingDate) count++;
        return count;
    };
    const detailItemCount = countDetailItems();

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2, borderRadius: 2, backgroundColor: '#F8FAFC',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: 360, maxWidth: '100%',
                flexShrink: 0, display: 'flex', flexDirection: 'column',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        backgroundColor: '#7C3AED', borderRadius: '6px', p: 0.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                        <AccountBalanceIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 600, color: '#1E293B', fontSize: '1rem', lineHeight: 1.3, wordBreak: 'break-word'
                        }}>
                            {chapterLabel}
                        </Typography>
                        {caseNumber && (
                            <Typography title={caseNumber} sx={{
                                color: '#64748B', fontSize: '0.7rem', fontFamily: 'monospace', mt: 0.25,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                                Case #{caseNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {(filingDate || !isOpen) && (
                        <Chip
                            label={filingDate ? `Filed ${filingDate}` : 'Closed'}
                            size="small"
                            sx={{
                                backgroundColor: filingDate ? '#F5F3FF' : '#DCFCE7',
                                color: filingDate ? '#7C3AED' : '#16A34A',
                                fontSize: '0.7rem', fontWeight: 600, height: '22px',
                            }}
                        />
                    )}
                    {detailItemCount > 0 && (
                        <Box onClick={() => setDetailsExpanded(!detailsExpanded)} sx={{
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: '50%', width: 24, height: 24, '&:hover': { backgroundColor: '#F5F3FF' }
                        }}>
                            <ChevronRightIcon sx={{
                                color: '#1E293B', fontSize: 20,
                                transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease'
                            }} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Debtor name as subtitle */}
            {subtitleName && (
                <Typography sx={{ color: '#374151', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.3 }}>
                    {subtitleName}
                </Typography>
            )}

            {/* Duplicate badge */}
            {alsoShownIn.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
                    {alsoShownIn.map((badge, i) => {
                        const label = typeof badge === 'string' ? badge : badge.label;
                        const targetName = typeof badge === 'string' ? null : badge.targetName;
                        return (
                            <Chip key={i} label={label} size="small"
                                onClick={targetName && onBadgeClick ? (e) => { e.stopPropagation(); onBadgeClick(targetName); } : undefined}
                                sx={{
                                    backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: '0.65rem', fontWeight: 500,
                                    height: '20px', cursor: targetName && onBadgeClick ? 'pointer' : 'default',
                                    '& .MuiChip-label': { px: 1 },
                                    '&:hover': targetName && onBadgeClick ? { backgroundColor: '#FECACA' } : {}
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Details Section */}
            <Collapse in={detailsExpanded}>
                <Divider sx={{ my: 1.5 }} />
                <Box>
                    {courtName && <BulletItem label="Court" text={courtName} />}
                    {courtLocation && <BulletItem label="Location" text={courtLocation} />}
                    {judgeName && <BulletItem label="Judge" text={judgeName} />}
                    {filerType && <BulletItem label="Filer type" text={filerType} />}
                    {filingStatus && <BulletItem label="Filing status" text={filingStatus} />}
                    {caseStatus && <BulletItem label="Case status" text={caseStatus} />}
                    {assetsForUnsecured && <BulletItem label="Assets for unsecured" text={assetsForUnsecured} />}

                    {uniqueDebtors.length > 0 && (
                        <>
                            <DetailSectionLabel label="Debtors:" />
                            {uniqueDebtors.map((d, i) => (
                                <BulletItem key={i} text={`${toTitleCase(d.name)}${d.address ? ` - ${d.address}` : ''}`} />
                            ))}
                        </>
                    )}

                    {trustees.length > 0 && (
                        <>
                            <DetailSectionLabel label="Trustees:" />
                            {trustees.map((t, i) => (
                                <BulletItem key={i} text={toTitleCase(t.name)} />
                            ))}
                        </>
                    )}

                    {statusHistory && statusHistory.length > 0 && (
                        <>
                            <DetailSectionLabel label="Status History:" />
                            {statusHistory.slice(0, 5).map((s, i) => (
                                <BulletItem key={i} text={`${s.type}${s.date ? ` (${new Date(s.date).toLocaleDateString()})` : ''}`} />
                            ))}
                        </>
                    )}

                    {meetingDate && <BulletItem label="Meeting" text={`${meetingDate}${meetingAddress ? ` at ${meetingAddress}` : ''}`} />}
                </Box>
            </Collapse>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto', pt: 1.5 }}>
                <SourceButton sources={source} />
            </Box>
        </Paper>
    );
};

// Card for Lien/Judgment Records
export const LienJudgmentRecordCard = ({
    caseNumber,
    originFilingType,
    originFilingNumber,
    filingDate,
    amount,
    eviction,
    jurisdiction,
    state,
    debtors = [],
    creditors = [],
    lienFilings = [],
    entityAddresses = [],
    status,
    source = [],
    alsoShownIn = [],
    onBadgeClick,
    partiesList = [],
}) => {
    const isOpen = status === 'open';
    const [detailsExpanded, setDetailsExpanded] = React.useState(false);

    const detailItemCount = _.filter([amount, originFilingType, jurisdiction, eviction === 'Y'], Boolean).length
        + debtors.length + creditors.length + entityAddresses.length + lienFilings.length;

    // Title: "[State] Lien" or "[State] Judgement" based on filing type
    const filingTypeUpper = (originFilingType || '').toUpperCase();
    const isLien = filingTypeUpper.includes('LIEN') || filingTypeUpper.includes('WARRANT');
    const isJudgment = filingTypeUpper.includes('JUDGMENT') || filingTypeUpper.includes('JUDGEMENT');
    // Default to "Litigation" for ambiguous lien_judgment record types (e.g. CIVIL NEW FILING)
    const recordLabel = isLien ? 'Lien' : isJudgment ? 'Judgement' : 'Litigation';
    const title = state ? `${getStateName(state)} ${recordLabel}` : recordLabel;

    // Subtitle: "Creditor vs. Debtor" like civil case names
    const creditorName = creditors.length > 0 ? toTitleCase(creditors[0].name) : null;
    const debtorName = debtors.length > 0 ? toTitleCase(debtors[0].name) : null;
    const subtitle = creditorName && debtorName
        ? `${creditorName} vs. ${debtorName}`
        : creditorName || debtorName || null;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2, borderRadius: 2, backgroundColor: '#F8FAFC',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)', width: 360, maxWidth: '100%',
                flexShrink: 0, display: 'flex', flexDirection: 'column',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Box sx={{
                        backgroundColor: '#D97706', borderRadius: '6px', p: 0.5,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                        <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{
                            fontWeight: 600, color: '#1E293B', fontSize: '1rem', lineHeight: 1.3, wordBreak: 'break-word'
                        }}>
                            {title}
                        </Typography>
                        {(caseNumber || originFilingNumber) && (
                            <Typography title={caseNumber || originFilingNumber} sx={{
                                color: '#64748B', fontSize: '0.7rem', fontFamily: 'monospace', mt: 0.25,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>
                                #{caseNumber || originFilingNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {(filingDate || !isOpen) && (
                        <Chip
                            label={filingDate ? `Filed ${filingDate}` : 'Closed'}
                            size="small"
                            sx={{
                                backgroundColor: filingDate ? '#FFFBEB' : '#DCFCE7',
                                color: filingDate ? '#D97706' : '#16A34A',
                                fontSize: '0.7rem', fontWeight: 600, height: '22px',
                            }}
                        />
                    )}
                    {detailItemCount > 0 && (
                    <Box onClick={() => setDetailsExpanded(!detailsExpanded)} sx={{
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '50%', width: 24, height: 24, '&:hover': { backgroundColor: '#FFFBEB' }
                    }}>
                        <ChevronRightIcon sx={{
                            color: '#1E293B', fontSize: 20,
                            transform: detailsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                        }} />
                    </Box>
                    )}
                    </Box>
                    {amount && (
                        <Chip
                            label={`$${Number(amount).toLocaleString()}`}
                            size="small"
                            sx={{
                                backgroundColor: '#FFFBEB',
                                color: '#D97706',
                                fontSize: '0.7rem', fontWeight: 600, height: '22px',
                                mr: '28px',
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Creditor vs. Debtor */}
            {subtitle && (
                <Typography sx={{
                    color: '#374151', fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.3
                }}>
                    {subtitle}
                </Typography>
            )}

            {/* Duplicate badge */}
            {alsoShownIn.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, mb: 1 }}>
                    {alsoShownIn.map((badge, i) => {
                        const badgeLabel = typeof badge === 'string' ? badge : badge.label;
                        const targetName = typeof badge === 'string' ? null : badge.targetName;
                        return (
                            <Chip key={i} label={badgeLabel} size="small"
                                onClick={targetName && onBadgeClick ? (e) => { e.stopPropagation(); onBadgeClick(targetName); } : undefined}
                                sx={{
                                    backgroundColor: '#FEE2E2', color: '#DC2626', fontSize: '0.65rem', fontWeight: 500,
                                    height: '20px', cursor: targetName && onBadgeClick ? 'pointer' : 'default',
                                    '& .MuiChip-label': { px: 1 },
                                    '&:hover': targetName && onBadgeClick ? { backgroundColor: '#FECACA' } : {}
                                }}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Details Section */}
            <Collapse in={detailsExpanded}>
                <Divider sx={{ my: 1.5 }} />
                <Box>
                    {amount && <BulletItem label="Amount" text={`$${Number(amount).toLocaleString()}`} />}
                    {originFilingType && <BulletItem label="Type" text={toTitleCase(originFilingType)} />}
                    {jurisdiction && <BulletItem label="Jurisdiction" text={jurisdiction} />}
                    {eviction === 'Y' && <BulletItem label="Eviction" text="Yes" isHighlighted />}

                    {debtors.length > 0 && (
                        <>
                            <DetailSectionLabel label="Debtors:" />
                            {debtors.map((d, i) => (
                                <Box key={i}>
                                    <BulletItem text={toTitleCase(d.name)} />
                                    {d.address && <BulletItem text={d.address} />}
                                </Box>
                            ))}
                        </>
                    )}

                    {creditors.length > 0 && (
                        <>
                            <DetailSectionLabel label="Creditors:" />
                            {creditors.map((c, i) => (
                                <Box key={i}>
                                    <BulletItem text={toTitleCase(c.name)} />
                                    {c.address && <BulletItem text={c.address} />}
                                </Box>
                            ))}
                        </>
                    )}

                    {entityAddresses.length > 0 && (
                        <>
                            <DetailSectionLabel label="Addresses:" />
                            {entityAddresses.map((addr, i) => (
                                <BulletItem key={i} text={addr} />
                            ))}
                        </>
                    )}

                    {lienFilings && lienFilings.length > 0 && (
                        <>
                            <DetailSectionLabel label="Filings:" />
                            {lienFilings.map((f, i) => (
                                <BulletItem key={i} text={`${f.type || 'Filing'}${f.number ? ` #${f.number}` : ''}${f.date ? ` (${new Date(f.date).toLocaleDateString()})` : ''}${f.agency ? ` - ${f.agency}` : ''}`} />
                            ))}
                        </>
                    )}
                </Box>
            </Collapse>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto', pt: 1.5 }}>
                <SourceButton sources={source} />
            </Box>
        </Paper>
    );
};

// Dispatcher component to render the right card based on record type, with an
// optional remove button in edit mode.
const RecordCard = ({ record, onBadgeClick, litigations, onUpdateLitigations }) => {
    let card;
    if (record.recordType === 'bankruptcy') {
        card = <BankruptcyRecordCard {...record} onBadgeClick={onBadgeClick} />;
    } else if (record.recordType === 'lien_judgment') {
        card = <LienJudgmentRecordCard {...record} onBadgeClick={onBadgeClick} />;
    } else {
        card = <CivilCourtRecordCard {...record} onBadgeClick={onBadgeClick} />;
    }

    if (!onUpdateLitigations) return card;

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            {card}
            <RemoveLitigationRecordButton
                caseNumber={record.caseNumber}
                originFilingNumber={record.originFilingNumber}
                recordId={record.id}
                recordType={record.recordType}
                originFilingType={record.originFilingType}
                litigations={litigations}
                onUpdate={onUpdateLitigations}
            />
        </Box>
    );
};

// Section header for grouping (clickable to collapse/expand)
export const SectionHeader = ({ title, icon: Icon, count, isEntity = true, expanded = true, onToggle }) => (
    <Box
        onClick={onToggle}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: expanded ? 2 : 1,
            p: 2,
            border: '1px solid #E2E6F0',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: 'rgba(242, 243, 251, 0.4)' }
        }}
    >
        <Box sx={{
            backgroundColor: isEntity ? '#E0E2F1' : '#dfeef5',
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Icon sx={{ color: colors.primary, fontSize: 20 }} />
        </Box>
        <Typography sx={{
            fontWeight: typography.fontWeights.semibold,
            color: colors.primary,
            fontSize: '1rem',
            flex: 1
        }}>
            {title}
            {count > 0 && (
                <Typography component="span" sx={{
                    color: colors.primary,
                    fontWeight: 600,
                    fontSize: '1rem',
                    ml: 0.5
                }}>
                    ({count})
                </Typography>
            )}
        </Typography>
        <ChevronRightIcon sx={{
            color: colors.primary,
            fontSize: 22,
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
        }} />
    </Box>
);

// Empty state for sections with no records
export const EmptyState = ({ message }) => (
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 2,
        px: 1
    }}>
        <Box sx={{
            backgroundColor: '#10B981',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <AddIcon sx={{ fontSize: 14, color: 'white' }} />
        </Box>
        <Typography sx={{ color: '#374151', fontSize: '0.875rem' }}>
            {message}
        </Typography>
    </Box>
);

// Check if a lien_judgment record is actually a lien/judgment (vs a litigation)
export const isLienOrJudgment = (record) => {
    const ft = (record.origin_filing_type || '').toUpperCase();
    return ft.includes('LIEN') || ft.includes('WARRANT') || ft.includes('JUDGMENT') || ft.includes('JUDGEMENT');
};

export const transformLitigationRecord = (record) => {
    return {
        id: record.id || null,
        caseNumber: record.case_number || null,
        caseName: record.case_name || null,
        caseType: record.case_type || null,
        caseSubtype: record.case_subtype || null,
        originalCaseType: record.original_case_type || null,
        partyType: record.party_type || 'Unknown',
        partyName: record.party_name || null,
        allParties: record.all_parties || null,
        partiesList: record.parties_list || [],
        causeOfAction: record.cause_of_action || null,
        filingDate: record.filing_date ? new Date(record.filing_date).toLocaleDateString() : null,
        filingDateRaw: record.filing_date || null,
        jurisdiction: record.jurisdiction || null,
        state: record.state || null,
        county: record.county || null,
        city: record.city || null,
        caseEvents: record.case_events || [],
        caseEventsDetailed: record.case_events_detailed || [],
        status: record.status || 'open',
        source: record.source || [{ source_url: "LexisNexis", fetched_at: new Date().toISOString() }],
        nodeId: record.node_id || null,
        entityTypeDescription: record.entity_type_description || null,
        entityAddresses: record.entity_addresses || [],
        suitAmount: record.suit_amount || null,
        awardAmount: record.award_amount || null,
        judgmentDate: record.judgment_date ? new Date(record.judgment_date).toLocaleDateString() : null,
        judgmentType: record.judgment_type || null,
        judgmentDisposition: record.judgment_disposition || null,
        disposition: record.disposition || null,
        dispositionDate: record.disposition_date ? new Date(record.disposition_date).toLocaleDateString() : null,
        ruling: record.ruling || null,
        // Record type
        recordType: record.record_type || 'civil_court',
        courtLevel: record.court_level || null,
        // Bankruptcy fields
        chapter: record.chapter || null,
        originalChapter: record.original_chapter || null,
        filingStatus: record.filing_status || null,
        caseStatus: record.case_status || null,
        courtName: record.court_name || null,
        courtLocation: record.court_location || null,
        judgeName: record.judge_name || null,
        filerType: record.filer_type || null,
        debtors: record.debtors || [],
        trustees: record.trustees || [],
        statusHistory: record.status_history || [],
        meetingDate: record.meeting_date ? new Date(record.meeting_date).toLocaleDateString() : null,
        meetingAddress: record.meeting_address || null,
        assetsForUnsecured: record.assets_for_unsecured || null,
        selfRepresented: record.self_represented || null,
        // Lien fields
        originFilingNumber: record.origin_filing_number || null,
        originFilingType: record.origin_filing_type || null,
        amount: record.amount || null,
        eviction: record.eviction || null,
        creditors: record.creditors || [],
        lienFilings: record.lien_filings || [],
    };
};

// Filter out records with no identifying information at all
export const isValidRecord = (record) => {
    if (record.record_type === 'bankruptcy') {
        return !!record.case_number;
    }
    if (record.record_type === 'lien_judgment') {
        return !!(record.case_number || record.origin_filing_number);
    }
    if (!record.case_number && !record.case_name && !record.party_name) return false;
    return true;
};

// Deduplicate records by (case_number, node_id) and merge party information
// Same case can appear under different entities/individuals
export const deduplicateRecords = (records) => {
    const caseMap = new Map();
    const partySetMap = new Map();

    records.forEach(record => {
        const caseNumber = record.case_number;
        const nodeId = record.node_id || '';
        if (!caseNumber) {
            const key = `no_case_${Math.random()}`;
            const newRecord = { ...record };
            if (!newRecord.parties_list?.length && record.party_name) {
                newRecord.parties_list = [{ name: record.party_name, role: record.party_type || '' }];
            }
            caseMap.set(key, newRecord);
            return;
        }

        const dedupKey = `${caseNumber}::${nodeId}`;

        if (caseMap.has(dedupKey)) {
            const existing = caseMap.get(dedupKey);
            const partySet = partySetMap.get(dedupKey);
            const newParty = record.party_name || '';

            if (newParty && !partySet.has(newParty)) {
                partySet.add(newParty);
                if (!existing.parties_list) existing.parties_list = [];
                existing.parties_list.push({ name: newParty, role: record.party_type || '' });
            }

            if (!existing.cause_of_action && record.cause_of_action) {
                existing.cause_of_action = record.cause_of_action;
            }
            if (!existing.filing_date && record.filing_date) {
                existing.filing_date = record.filing_date;
            }
            if ((!existing.case_events || existing.case_events.length === 0) && record.case_events?.length > 0) {
                existing.case_events = record.case_events;
            }
        } else {
            const partySet = new Set();
            const newRecord = { ...record };

            if (newRecord.parties_list?.length) {
                newRecord.parties_list.forEach(p => partySet.add(p.name));
            } else {
                newRecord.parties_list = [];
                if (record.party_name) {
                    partySet.add(record.party_name);
                    newRecord.parties_list.push({ name: record.party_name, role: record.party_type || '' });
                }
            }

            caseMap.set(dedupKey, newRecord);
            partySetMap.set(dedupKey, partySet);
        }
    });

    return Array.from(caseMap.values());
};

// Check if a case mentions the company name in its parties or case name
export const caseReferencesCompany = (record, entityName) => {
    if (!entityName) return false;
    const name = entityName.toLowerCase().trim();
    // Also try a simplified version without common legal suffixes
    const simplified = name.replace(/\b(inc|llc|ltd|corp|co|company|incorporated|limited|corporation|lp|llp|pllc|plc|group|holdings)\b\.?/gi, '').trim();

    const check = (str) => {
        if (!str) return false;
        const lower = str.toLowerCase();
        return lower.includes(name) || (simplified && simplified.length > 2 && lower.includes(simplified));
    };

    if (check(record.case_name)) return true;
    if (check(record.party_name)) return true;
    if (check(record.all_parties)) return true;
    if (record.parties_list?.some(p => check(p.name))) return true;
    return false;
};

const transformLitigationsData = (litigations, entity, ubos) => {
    const filteredRecords = _.filter([
        ...(litigations?.open || []),
        ...(litigations?.closed || [])
    ], r => isValidRecord(r) && (r.record_type !== 'lien_judgment' || !isLienOrJudgment(r)));

    const allRecords = deduplicateRecords(filteredRecords);

    if (allRecords.length === 0) {
        return {
            entity: {
                name: entity?.legal_name || 'Business Entity',
                records: []
            },
            individuals: _.map(ubos || [], ubo => ({
                name: ubo.name,
                role: ubo.role || 'Associated Person',
                records: []
            })),
            sources: [{ source_url: "LexisNexis", fetched_at: new Date().toISOString() }]
        };
    }

    const entityId = entity?.business_entity_id;
    const entityName = entity?.legal_name || '';

    // Build per-node party type lookup for defendant filtering
    // Key: "case_number::node_id", Value: party_type (uppercase)
    const nodePartyTypeMap = new Map();
    allRecords.forEach(record => {
        if (record.record_type !== 'civil_court') return;
        const cn = record.case_number;
        if (!cn) return;
        const nodeId = record.node_id || '';
        const key = `${cn}::${nodeId}`;
        if (!nodePartyTypeMap.has(key)) {
            nodePartyTypeMap.set(key, (record.party_type || '').toUpperCase());
        }
    });

    // Build set of UBO node_ids for quick lookup
    const uboNodeIds = new Set(_.filter(_.map(ubos || [], ubo => ubo.node_id), Boolean));

    // Deduplicate by case_number globally, merging parties across duplicates.
    // A case may still appear in multiple sections (entity + UBO) with "Also shown under" badges.
    const globalCaseMap = new Map();
    allRecords.forEach(record => {
        const cn = record.case_number;
        if (!cn) {
            // Records without case_number stay as-is
            globalCaseMap.set(`no_case_${Math.random()}`, { record, nodeIds: new Set([record.node_id || '']) });
            return;
        }
        if (globalCaseMap.has(cn)) {
            const entry = globalCaseMap.get(cn);
            entry.nodeIds.add(record.node_id || '');
            // Merge parties from duplicate
            const existing = entry.record;
            if (record.parties_list?.length) {
                const existingNames = new Set(_.map(existing.parties_list || [], p => p.name));
                record.parties_list.forEach(p => {
                    if (!existingNames.has(p.name)) {
                        existing.parties_list = existing.parties_list || [];
                        existing.parties_list.push(p);
                        existingNames.add(p.name);
                    }
                });
            }
            // Fill missing fields
            if (!existing.cause_of_action && record.cause_of_action) existing.cause_of_action = record.cause_of_action;
            if (!existing.filing_date && record.filing_date) existing.filing_date = record.filing_date;
            if ((!existing.case_events || !existing.case_events.length) && record.case_events?.length) existing.case_events = record.case_events;
            if ((!existing.case_events_detailed || !existing.case_events_detailed.length) && record.case_events_detailed?.length) existing.case_events_detailed = record.case_events_detailed;
            if (!existing.suit_amount && record.suit_amount) existing.suit_amount = record.suit_amount;
            if (!existing.award_amount && record.award_amount) existing.award_amount = record.award_amount;
        } else {
            globalCaseMap.set(cn, { record: { ...record }, nodeIds: new Set([record.node_id || '']) });
        }
    });

    // Helper: check if a civil court record has a defendant role for any of the given node_ids
    const isDefendantForNodes = (record, relevantNodeIds) => {
        if (record.record_type !== 'civil_court') return true;
        const cn = record.case_number;
        if (!cn) return false;
        return relevantNodeIds.some(nid => {
            const pt = nodePartyTypeMap.get(`${cn}::${nid}`) || '';
            return pt.includes('DEFEND');
        });
    };

    const entityRecords = [];
    const uboMap = new Map();
    (ubos || []).forEach(ubo => {
        uboMap.set(ubo.node_id, {
            name: ubo.name,
            role: ubo.role || 'Associated Person',
            records: []
        });
    });

    // Assign each case to ALL relevant sections and track duplicates
    globalCaseMap.forEach(({ record, nodeIds }) => {
        const isEntityByNodeId = [...nodeIds].some(nid =>
            !nid || nid === entityId || nid === String(entityId) || !uboNodeIds.has(nid)
        );
        const belongsToEntity = isEntityByNodeId || caseReferencesCompany(record, entityName);

        // Find all UBOs this case belongs to
        const matchingUboIds = _.filter([...nodeIds], nid => uboMap.has(nid));

        // Build the list of all sections this case appears in
        const sections = [];
        if (belongsToEntity) sections.push({ type: 'entity', name: entityName });
        matchingUboIds.forEach(nid => {
            sections.push({ type: 'ubo', name: uboMap.get(nid).name });
        });

        // If case doesn't match anything, put it under entity
        if (sections.length === 0) {
            const entityNodeIds = _.filter([...nodeIds], nid => !uboNodeIds.has(nid));
            if (isDefendantForNodes(record, entityNodeIds)) {
                entityRecords.push(transformLitigationRecord(record));
            }
            return;
        }

        // Place in entity section with badge if also in UBO sections
        if (belongsToEntity) {
            const entityNodeIds = _.filter([...nodeIds], nid =>
                !nid || nid === entityId || nid === String(entityId) || !uboNodeIds.has(nid)
            );
            if (isDefendantForNodes(record, entityNodeIds)) {
                const transformed = transformLitigationRecord(record);
                if (matchingUboIds.length > 0) {
                    transformed.alsoShownIn = matchingUboIds.map(nid => {
                        const name = uboMap.get(nid).name;
                        return { label: `Also shown under ${toTitleCase(name)}`, targetName: name };
                    });
                }
                entityRecords.push(transformed);
            }
        }

        // Place in each matching UBO section with badge if also in other sections
        matchingUboIds.forEach(nid => {
            if (!isDefendantForNodes(record, [nid])) return;
            const transformed = transformLitigationRecord(record);
            const otherSections = [];
            if (belongsToEntity) otherSections.push({ label: `Also shown under ${toTitleCase(entityName)}`, targetName: entityName });
            matchingUboIds.filter(id => id !== nid).forEach(id => {
                const name = uboMap.get(id).name;
                otherSections.push({ label: `Also shown under ${toTitleCase(name)}`, targetName: name });
            });
            if (otherSections.length > 0) transformed.alsoShownIn = otherSections;
            uboMap.get(nid).records.push(transformed);
        });

        // If only entity matched (no UBOs), already handled above
    });

    // Sort newest filing date first, no date at the end
    const sortByFilingDate = (a, b) => {
        if (!a.filingDateRaw && !b.filingDateRaw) return 0;
        if (!a.filingDateRaw) return 1;
        if (!b.filingDateRaw) return -1;
        return new Date(b.filingDateRaw) - new Date(a.filingDateRaw);
    };

    entityRecords.sort(sortByFilingDate);
    const individuals = Array.from(uboMap.values());
    individuals.forEach(ind => ind.records.sort(sortByFilingDate));

    return {
        entity: {
            name: entity?.legal_name || 'Business Entity',
            records: entityRecords
        },
        individuals,
        sources: [{ source_url: "LexisNexis", fetched_at: new Date().toISOString() }]
    };
};

const LitigationsTab = ({ litigations, entity, ubos, onUpdateLitigations }) => {
    const data = React.useMemo(() => {
        if (litigations && (litigations.open?.length > 0 || litigations.closed?.length > 0)) {
            return transformLitigationsData(litigations, entity, ubos);
        }
        return null;
    }, [litigations, entity, ubos]);

    const [expandedSections, setExpandedSections] = React.useState({});
    const sectionRefs = React.useRef({});

    const isSectionExpanded = (key) => expandedSections[key] !== false; // default expanded
    const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !isSectionExpanded(key) }));

    const handleBadgeClick = React.useCallback((targetName) => {
        if (!data) return;
        const normalizedTarget = targetName.toLowerCase().trim();

        // Check if it's the entity section
        if (data.entity.name.toLowerCase().trim() === normalizedTarget) {
            setExpandedSections(prev => ({ ...prev, entity: true }));
            setTimeout(() => {
                sectionRefs.current['entity']?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            return;
        }

        // Find matching individual section
        const visibleIndividuals = _.filter(data.individuals, ind => {
                const name = (ind.name || '').toLowerCase().trim();
                return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
            })
            .sort((a, b) => b.records.length - a.records.length);

        const matchIdx = visibleIndividuals.findIndex(ind =>
            ind.name.toLowerCase().trim() === normalizedTarget
        );

        if (matchIdx !== -1) {
            const sectionKey = `individual-${matchIdx}`;
            setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));
            setTimeout(() => {
                sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    }, [data]);

    if (!data) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ color: colors.grey }}>
                    No defendant litigation data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Entity Section */}
            <Box ref={el => sectionRefs.current['entity'] = el}>
                <SectionHeader
                    title={toTitleCase(data.entity.name)}
                    icon={BusinessIcon}
                    count={data.entity.records.length}
                    isEntity={true}
                    expanded={isSectionExpanded('entity')}
                    onToggle={() => toggleSection('entity')}
                />
            </Box>
            <Collapse in={isSectionExpanded('entity')}>
                {data.entity.records.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, alignItems: 'flex-start' }}>
                        {_.map(data.entity.records, (record, index) => (
                            <RecordCard
                                key={index}
                                record={record}
                                onBadgeClick={handleBadgeClick}
                                litigations={litigations}
                                onUpdateLitigations={onUpdateLitigations}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ mb: 4 }}>
                        <EmptyState message="No records found" />
                    </Box>
                )}
            </Collapse>

            {/* Individual Sections - only show persons with cases */}
            {_.map(_.filter(data.individuals, ind => {
                    const name = (ind.name || '').toLowerCase().trim();
                    return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
                })
                .sort((a, b) => b.records.length - a.records.length), (individual, idx) => (
                <Box key={idx}>
                    <Box ref={el => sectionRefs.current[`individual-${idx}`] = el}>
                        <SectionHeader
                            title={`${toTitleCase(individual.name)} · ${toTitleCase(individual.role)}`}
                            icon={PersonIcon}
                            count={individual.records.length}
                            isEntity={false}
                            expanded={isSectionExpanded(`individual-${idx}`)}
                            onToggle={() => toggleSection(`individual-${idx}`)}
                        />
                    </Box>
                    <Collapse in={isSectionExpanded(`individual-${idx}`)}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4, alignItems: 'flex-start' }}>
                            {_.map(individual.records, (record, index) => (
                                <RecordCard
                                key={index}
                                record={record}
                                onBadgeClick={handleBadgeClick}
                                litigations={litigations}
                                onUpdateLitigations={onUpdateLitigations}
                            />
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            ))}

            {/* Sources */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <SourceButton sources={data.sources || []} />
            </Box>
        </Box>
    );
};

export const getLitigationsDisplayCount = (litigations, entity, ubos) => {
    if (!litigations || (!litigations.open?.length && !litigations.closed?.length)) return 0;
    const data = transformLitigationsData(litigations, entity, ubos);
    let count = data.entity.records.length;
    _.filter(data.individuals, ind => {
            const name = (ind.name || '').toLowerCase().trim();
            return name && name !== 'unknown unknown' && name !== 'unknown' && ind.records.length > 0;
        })
        .forEach(ind => { count += ind.records.length; });
    return count;
};

export default LitigationsTab;
